import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useInfiniteQuery } from 'react-query'
import posthog from 'posthog-js'
import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import { fetchAdminMembers } from 'services/fund'
import { faPlain } from 'utilities/helper/format'
import { ScoreFromFilter, useScoreFrom } from 'components/memberScore'
import { MemberRow, SORTS, SortPills, TAGS } from './MemberRows'
import useScoreExplainer from './useScoreExplainer'

/** فهرست اعضا — the full list behind the dashboard's «اعضا» card (pwa#36). */
export default function FundMembers() {
    const { id } = useParams()
    const [q, setQ] = useState('')
    const [debouncedQ, setDebouncedQ] = useState('')
    const [sort, setSort] = useState<TMembersSort>('score')
    const [tag, setTag] = useState<TMembersTag>('all')
    const [scoreFrom] = useScoreFrom(id)

    // the scan behind the sort keys is the expensive one; typing must not
    // fire it per keystroke
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQ(q), 350)
        return () => clearTimeout(timer)
    }, [q])

    /**
     * Infinite, not paged.
     *
     * «نمایش بیشتر» used to swap the visible page for the next one, so a
     * manager who wanted rows 31–60 lost rows 1–30 and the button read as a
     * lie. A list you scroll is the only shape that matches what the button
     * says. Changing the search, sort, filter or «امتیاز از» starts a fresh
     * key, which is right — those are new questions, not more of the same
     * answer.
     */
    const {
        data,
        isLoading,
        isFetching,
        isPreviousData,
        fetchNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery(
        ['admin-members-list', id, debouncedQ, sort, tag, scoreFrom],
        ({ pageParam = 1 }) =>
            fetchAdminMembers(String(id), {
                q: debouncedQ,
                sort,
                tag,
                page: pageParam,
                scoreFrom,
            }),
        {
            getNextPageParam: (lastPage, allPages) =>
                lastPage.has_next ? allPages.length + 1 : undefined,
            keepPreviousData: true,
            staleTime: 60 * 1000,
            enabled: !!id,
        }
    )

    useEffect(() => {
        posthog.capture('members_list_viewed', { fund_id: id })
    }, [id])

    const pages = data?.pages ?? []
    // counts, totals and the score multipliers are the same on every page;
    // the first one is as good as any and is always present
    const meta = pages[0]
    const rows = pages.flatMap((page) => page.results)
    const hasNext = pages.length > 0 && pages[pages.length - 1].has_next

    const explainer = useScoreExplainer(meta)
    const spec = SORTS.find((option) => option.value === sort) ?? SORTS[0]
    const max = rows.reduce((best, row) => Math.max(best, spec.read(row)), 0)

    return (
        <div className=" min-h-screen bg-canvas pb-8">
            <Header title="اعضای صندوق" />

            <div className=" p-3">
                <div className=" rounded-2xl border border-stroke bg-white p-4 shadow-card">
                    <p className=" text-[11px] text-text-secondary">
                        {meta
                            ? tag === 'all'
                                ? `${faPlain.format(
                                      meta.members_count
                                  )} عضو فعال`
                                : `${faPlain.format(
                                      meta.count
                                  )} عضو از ${faPlain.format(
                                      meta.members_count
                                  )}`
                            : '...'}
                    </p>

                    <input
                        value={q}
                        onChange={(event) => setQ(event.target.value)}
                        placeholder="جستجوی نام یا شمارهٔ همراه"
                        className=" mt-2.5 w-full rounded-[10px] bg-canvas px-3 py-3 text-xs text-text-body outline-none placeholder:text-text-muted"
                    />

                    <div className=" mt-2.5 flex flex-wrap gap-1.5">
                        {TAGS.map((option) => {
                            const active = option.value === tag
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => {
                                        posthog.capture(
                                            'members_list_filtered',
                                            {
                                                fund_id: id,
                                                tag: option.value,
                                            }
                                        )
                                        setTag(option.value)
                                    }}
                                    className={` min-h-[36px] rounded-full border px-3 text-[11px] ${
                                        active
                                            ? ' border-brand bg-brand-soft font-bold text-brand-deep'
                                            : ' border-stroke bg-white text-text-secondary'
                                    }`}
                                >
                                    {option.label}
                                    {meta
                                        ? ` (${faPlain.format(
                                              meta.counts[option.value] ?? 0
                                          )})`
                                        : ''}
                                </button>
                            )
                        })}
                    </div>

                    {/* the label sits ABOVE the pills, not beside them: five
                        pills and a label do not share a phone's width, and
                        beside them the row simply drew outside the card */}
                    <div className=" mt-3">
                        <p className=" text-[11px] text-text-secondary">
                            مرتب‌سازی
                        </p>
                        <div className=" mt-1.5">
                            <SortPills
                                sort={sort}
                                onChange={(value) => {
                                    posthog.capture('members_list_sorted', {
                                        fund_id: id,
                                        sort: value,
                                    })
                                    setSort(value)
                                }}
                            />
                        </div>
                        {/* under مرتب‌سازی because it is the other half of
                            the same question — what «امتیاز» ranks by */}
                        <ScoreFromFilter
                            moneypoolId={id}
                            appliedFrom={meta?.start_date}
                            busy={isFetching && isPreviousData}
                            surface="members"
                        />
                    </div>

                    {isLoading && !meta ? (
                        <DotsLoading />
                    ) : (
                        <div className=" mt-2 flex flex-col">
                            {rows.map((row) => (
                                <MemberRow
                                    key={row.poolship_id}
                                    row={row}
                                    sort={sort}
                                    max={max}
                                    moneypoolId={id}
                                    onExplainScore={explainer.open}
                                />
                            ))}
                            {meta && rows.length === 0 && (
                                <p className=" my-6 text-center text-xs text-text-muted">
                                    عضوی با این فیلتر پیدا نشد.
                                </p>
                            )}
                        </div>
                    )}

                    {hasNext && (
                        <button
                            type="button"
                            disabled={isFetchingNextPage}
                            onClick={() => fetchNextPage()}
                            className=" mt-3 min-h-[44px] w-full rounded-[10px] bg-canvas text-xs font-bold text-brand-deep disabled:opacity-60"
                        >
                            {isFetchingNextPage
                                ? 'در حال بارگذاری…'
                                : 'نمایش بیشتر'}
                        </button>
                    )}
                </div>
            </div>
            {explainer.element}
        </div>
    )
}
